import {
  RefObject,
  CSSProperties,
  useCallback,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useResizeDetector } from "react-resize-detector";

import { Tooltip } from "../Tooltip";

export interface Props {
  rows?: number;
  text: string;
  tooltipMaxWidth?: string | number;
  customStyle?: CSSProperties;
  disableTooltip?: boolean;
  className?: string;
  tooltipClass?: string;
  onClick?: () => void;
  overrideTooltipText?: ReactNode
}

const EllipsisTextWithTooltip = ({
  rows = 1,
  text,
  tooltipMaxWidth,
  disableTooltip,
  customStyle,
  className = "",
  tooltipClass,
  onClick,
  overrideTooltipText,
}: Props) => {
  const { ref: divRef, width } = useResizeDetector({
    refreshMode: "debounce",
  });

  const [isHorizontallyTruncated, setIsHorizontallyTruncated] = useState(false);
  const [isVerticallyTruncated, setIsVerticallyTruncated] = useState(false);

  const isSingleRow = rows === 1;

  const checkIfTruncated = useCallback((cellElement: HTMLElement | null) => {
    if (cellElement) {
      const { scrollWidth, clientWidth, scrollHeight, clientHeight } =
        cellElement;
      setIsHorizontallyTruncated(scrollWidth > clientWidth);

      // Some font-families have an additional height, this is to take account for that
      const safeHeightDifference = 3;
      setIsVerticallyTruncated(
        scrollHeight - safeHeightDifference > clientHeight
      );
    }
    return false;
  }, []);

  useEffect(() => {
    checkIfTruncated(divRef?.current);
  }, [checkIfTruncated, divRef, width, rows, text]);

  const shouldDisplayBlock =
    isSingleRow ||
    (!isSingleRow && isHorizontallyTruncated && !isVerticallyTruncated);

  return (
    <Tooltip
      maxWidth={tooltipMaxWidth}
      title={overrideTooltipText ?? text}
      visible={
        !disableTooltip && (isHorizontallyTruncated || isVerticallyTruncated)
      }
      onClick={onClick}
      customStyle={{
        display: shouldDisplayBlock ? "block" : "-webkit-box",
        whiteSpace: isSingleRow ? "nowrap" : "normal",
        overflow: "hidden",
        textOverflow: "ellipsis",
        WebkitLineClamp: rows,
        WebkitBoxOrient: "vertical",

        ...customStyle,
      }}
      divRef={divRef as RefObject<HTMLDivElement>}
      className={tooltipClass}
      contentClassName={className}
    >
      {text}
    </Tooltip>
  );
};

export default EllipsisTextWithTooltip;
